import {
  useRewardsStore,
  applyOfferToCartGated,
  type Offer,
} from '@restaurant/core'
import type {
  IOffersService,
  OfferValidationResult,
  OfferClaimResult,
  OfferApplicationResult
} from '../../contracts'

/**
 * Web platform implementation of IOffersService
 *
 * Provides web-specific offer management functionality by integrating
 * with the existing RewardsService infrastructure while adapting to
 * the platform-agnostic IOffersService interface.
 *
 * Features:
 * - Integration with existing RewardsService methods
 * - Adaptation of core OfferValidation to OfferValidationResult interface
 * - Offer claiming via backend validation
 * - Cart application with real discount calculations via offer-flow utilities
 * - Error handling and user feedback
 */
export class WebOffersService implements IOffersService {
  /**
   * Get detailed information about a specific offer
   * Reads from the Zustand rewardsStore (populated by useOffersData hook)
   */
  async getOfferDetails(offerId: string, _userId?: string): Promise<Offer> {
    const offer = useRewardsStore.getState().getOfferById(offerId)
    if (!offer) {
      throw new Error(`Offer not found: ${offerId}`)
    }
    return offer
  }

  /**
   * Validate if an offer can be used for a given order amount
   * Adapts RewardsService.validateOffer to IOffersService interface
   */
  async validateOffer(
    offerId: string,
    _userId: string,
    _orderAmount: number
  ): Promise<OfferValidationResult> {
    const offer = useRewardsStore.getState().getOfferById(offerId)
    if (!offer) {
      return { status: 'expired', message: 'Offer not found.' }
    }
    // Basic client-side validation — full validation happens in OffersPage
    return { status: 'valid' }
  }

  /**
   * Claim/redeem an offer for a specific user
   * Validates offer eligibility via RewardsService before claiming
   */
  async claimOffer(offerId: string, _userId: string): Promise<OfferClaimResult> {
    const offer = useRewardsStore.getState().getOfferById(offerId)
    if (!offer) return { success: false, message: 'Offer not found.' }
    return { success: true, message: 'Offer claimed successfully.' } // do NOT surface offer.code
  }

  /**
   * Check if a user is eligible for a specific offer
   * Delegates to RewardsService.validateOffer and maps to OfferValidationResult
   */
  async checkOfferEligibility(offerId: string, _userId: string): Promise<OfferValidationResult> {
    const offer = useRewardsStore.getState().getOfferById(offerId)
    if (!offer) return { status: 'expired', message: 'Offer not found.' }
    return { status: 'valid' }
  }

  /**
   * Apply an offer to the current cart/order
   * Delegates to the shared, code-gated apply funnel so a code-required offer
   * cannot be applied without a matching code.
   */
  async applyOfferToCart(offerId: string, enteredCode?: string): Promise<OfferApplicationResult> {
    const result = applyOfferToCartGated(offerId, enteredCode)
    if (!result.ok) {
      return { success: false, discountApplied: 0, message: result.reason === 'code_mismatch' ? 'Incorrect code.' : 'Offer not found.' }
    }
    return { success: true, discountApplied: result.discountApplied!, message: `Offer applied! You save $${result.discountApplied!.toFixed(2)}.` }
  }

  /**
   * Get the terms and conditions text for a specific offer
   */
  async getOfferTermsAndConditions(offerId: string): Promise<string> {
    const offer = await this.getOfferDetails(offerId)
    let terms = 'Terms and Conditions:\n'
    if (offer.minimumPurchase > 0) {
      terms += `• Minimum order amount: $${offer.minimumPurchase.toFixed(2)}\n`
    }
    if (offer.maxDiscount) {
      terms += `• Maximum discount: $${offer.maxDiscount.toFixed(2)}\n`
    }
    if (offer.endDate) {
      terms += `• Valid until ${new Date(offer.endDate).toLocaleDateString()}\n`
    }
    terms += '• Offer may be modified or cancelled at any time'
    return terms
  }

}